import React, { useState, useEffect, useCallback } from 'react';
import { Switch, Route, NavLink } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Card, Nav, Spinner, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import api from '../../api';
import { getUploadsBaseUrl } from '../../api/axiosInstance';

import { toastrAdd } from './../../actions/ToastrActions';

import PartnerSettingsForm from  './PartnerSettingsForm/PartnerSettingsForm';
import PartnerMobilePreview from './PartnerMobilePreview/PartnerMobilePreview';
import HelpBlock from '../Layout/HelpBlock/HelpBlock';
import PartnerMaterials from './PartnerMaterials/PartnerMaterials';

function PartnerContainer(props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { url, eventID, partnerID, organizationID, isProspectumLiveEnabled, partnerPortalSettings } = props;
    const [loading, setLoading] = useState(true);
    const [mobileSettingsLoading, setMobileSettingsLoading] = useState(true);
    const [mobileSettings, setMobileSettings] = useState(null);
    const [partnersMeta, setPartnersMeta] = useState(null);
    const [logo, setLogo] = useState(null);
    const [deleteLogo, setDeleteLogo] = useState(false);
    const [saving, setSaving] = useState(false);
    const [partner, setPartner] = useState({});
    const uploadsBaseUrl = getUploadsBaseUrl();

    const _onLogoRemove = useCallback(() => {
        setLogo(null);
        setDeleteLogo(true);
    }, []);

    /**
     * Fetch partner information.
     */
    const _fetchPartner = useCallback(() => {
        if (partnerID) {
            setLoading(true);

            api.partner.getEventPartner(eventID, localStorage.getItem("partnerID")).then(response => {
                const { partners } = response.data.data;
                setPartner(partners);

                // Fetch logo material
                if(partners.logoMaterialID) {
                    api.material.getMaterial(partners.logoMaterialID).then(response => {
                        let { material } = response.data.data;
                        material.isNew = false;
                        material.preview = `${uploadsBaseUrl}${material.absolutePath}_XS`;
                        setLogo(material);
                    })
                }
            }).finally(() => setLoading(false))

            // set partner meta
            api.partner.getPartnersMeta()
                .then(response => {
                    setPartnersMeta(response.data.data.meta);
                });
        }
    }, [eventID, partnerID, uploadsBaseUrl]);

    /**
     * Fetch mobile settings for event.
     */
    const _fetchMobileSettings = useCallback(() => {
        api.mobile.getEventMobileSettings(eventID)
            .then(response => {
                setMobileSettings(response.data.data.mobileSettings);
                setMobileSettingsLoading(false);
            })
            .catch(error => {
                setMobileSettingsLoading(false);
            });
    }, [eventID]);

    /**
     * Save partner.
     */
    const _savePartner = (values) => {

        setSaving(true);

        // Extend existing partner object with new form values
        let partnerData =  {...partner, ...values};

        // If new logo is defined
        if(logo && logo.isNew) {
            const formData = new FormData();
            formData.append('file', logo);
            formData.append('name', logo.name);
            formData.append('organizationID', organizationID);
            formData.append('eventID', eventID);
            formData.append('materialFolderType', 4);
            // Upload material before saving partner
            api.material.upload(formData).then(response => {
                // Extend partner data with logoMaterialID
                partnerData.logoMaterialID = response.data.data.material.materialID;
                // Save partner
                api.partner.saveEventPartner(eventID, partnerData)
                    .then(response =>
                        _savePartnerSuccess(response))
                    .finally(() =>
                        setSaving(false));
            });
        } else {
            if (deleteLogo) {
                partnerData = { ...partnerData, logoMaterialID: null };
            }
            api.partner.saveEventPartner(eventID, partnerData)
                .then(response =>
                    _savePartnerSuccess(response))
                .catch(error => {
                    dispatch(toastrAdd({code: 0, details: `${t('general.details')}: ${error.errors[0].details}`, title: t('errors.general_form_error_title'), type: 'error'}));
                })
                .finally(() => {
                    setSaving(false);
                    if (deleteLogo) {
                        setDeleteLogo(false);
                    }
                })
        }
    }


    /**
     * @name _savePartnerSuccess
     * @desc Partner save success callback.
     * @param {object} response
     */
    const _savePartnerSuccess = response => {
        const { partner } = response.data.data;
        setPartner(partner);
        dispatch(toastrAdd({code: 0, details: t('events.partner_save_success_details', { partner: partner.name }), title: t('events.partner_save_success_title'), type: 'success'}));
    }


    /**
     * Fetch partner at component mount.
     */
    useEffect(() => {
        _fetchPartner();
        _fetchMobileSettings();
    }, [_fetchPartner, _fetchMobileSettings]);

    return (
        <React.Fragment>
            <h3>{t('events.organization_info')}</h3>
            <Card className="mt-0">
                <Card.Header>
                    {partner.name || <Spinner animation="border" size="sm" variant="primary" />}
                    <Nav variant="tabs">
                        <Nav.Item>
                            <NavLink to={`${url}`} strict exact className="nav-link">{t('general.info')}</NavLink>
                        </Nav.Item>
                        <Nav.Item>
                            <NavLink to={`${url}/materials`} className="nav-link">{t('general.materials')}</NavLink>
                        </Nav.Item>
                    </Nav>
                </Card.Header>
                <Card.Body>
                    {partner.partnerID ?
                        <Switch>
                            <Route path={`/events/:eventID/exhibitor/materials`}>
                                <PartnerMaterials
                                    {...props}
                                    uploadsBaseUrl={uploadsBaseUrl}
                                />
                            </Route>
                            <Route strict exact path={`${url}`}>
                                <Row>
                                    <Col md={8}>
                                        <PartnerSettingsForm
                                            logo={logo}
                                            setLogo={setLogo}
                                            loading={loading}
                                            saving={saving}
                                            partner={partner}
                                            partnersMeta={partnersMeta}
                                            onSubmit={_savePartner}
                                            onLogoRemove={_onLogoRemove}
                                            isProspectumLiveEnabled={isProspectumLiveEnabled}
                                            partnerPortalSettings={partnerPortalSettings}
                                        />
                                    </Col>
                                    {mobileSettings ?
                                        <Col md={4}>
                                            <HelpBlock>
                                                {t('instructions.partner_settings.help_block')}
                                            </HelpBlock>
                                            <PartnerMobilePreview
                                                partner={partner}
                                                logo={logo}
                                                mobileSettings={mobileSettings}
                                                loading={loading || mobileSettingsLoading}
                                            />
                                        </Col>
                                    : null}
                                </Row>

                            </Route>
                        </Switch>
                    :
                        <Spinner animation="border" variant="primary" size="sm" />
                    }
                </Card.Body>
            </Card>
        </React.Fragment>
    )
}

export default PartnerContainer;